// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import {
  ShipmentManifestEntry,
  ShipmentManifestProperty,
} from '@/audit/api/types';
import { AuditPropertyUpdateLabel } from '@/audit/components/audit-property-update-label';
import { AuditPropertyValue } from '@/audit/components/audit-property-value';
import { FileDownloadOutlined } from '@/constants/hardfin-standard-icons';
import { SHIPMENT_AUDIT_OPERATIONS } from '@/constants/shipment-audit-operation';
import { useToast } from '@/contexts/toast-context';
import { useFileSerials } from '@/file/hooks/use-file-serials';
import { downloadFileByKey } from '@/file/utilities/download-file-by-key';
import { pluralize } from '@/utilities/pluralize';
import { Box, Link, Stack, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';

/** Above this many serials the tooltip sends the reader to the download. */
const SERIAL_TOOLTIP_LIMIT = 20;

const TOOLTIP_ENTER_DELAY_MS = 300;
const TOOLTIP_MAX_WIDTH_PX = 440;
const TOOLTIP_COLUMNS = 2;

interface Props {
  property: ShipmentManifestProperty;

  /**
   * The shipment operation this manifest was recorded under. It decides whether
   * the serials are the pending set the 3PL reported or the confirmed set.
   */
  operation?: string;
}

interface SerialPreviewContentProps {
  serials: string[] | undefined;
  isPending: boolean;
  isError: boolean;
}

/**
 * Renders the hover preview of a serial file: the serials once they load, and
 * the state of the read until then.
 */
function SerialPreviewContent(props: SerialPreviewContentProps) {
  if (props.isError) {
    return (
      <Typography variant="caption">
        Could not load serials, click to download
      </Typography>
    );
  }

  if (props.isPending || props.serials === undefined) {
    return <Typography variant="caption">Loading serials…</Typography>;
  }

  if (props.serials.length === 0) {
    return <Typography variant="caption">No serials</Typography>;
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridAutoFlow: 'column',
        gridTemplateRows: `repeat(${Math.ceil(
          props.serials.length / TOOLTIP_COLUMNS
        )}, auto)`,
        columnGap: 3,
      }}
    >
      {props.serials.map((serial) => (
        <Typography key={serial} variant="caption">
          {serial}
        </Typography>
      ))}
    </Box>
  );
}

interface ManifestFileLinkProps {
  label: string;
  fileKey: string;

  /** How many serials the file holds, as recorded on the audit entry. */
  serialCount: number;
}

/**
 * Renders a serial count as a button that proxies the file download through the
 * API so it saves directly instead of opening a new tab. Hovering previews a
 * short list inline; the count on the entry decides that the list is too long
 * without reading the file.
 */
function ManifestFileLink(props: ManifestFileLinkProps) {
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const withinLimit = props.serialCount <= SERIAL_TOOLTIP_LIMIT;
  const preview = useFileSerials(props.fileKey, open && withinLimit);

  const handleClick = () => {
    downloadFileByKey(props.fileKey).catch((error) => {
      Sentry.captureException(error);
      showToast({
        element: <Typography>Could not download the serials file</Typography>,
        severity: 'error',
      });
    });
  };

  return (
    <Tooltip
      arrow
      enterDelay={TOOLTIP_ENTER_DELAY_MS}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: 8,
            maxWidth: TOOLTIP_MAX_WIDTH_PX,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1.5,
            px: 2,
            py: 1.5,
          },
        },
        arrow: { sx: { color: 'background.paper' } },
      }}
      title={
        withinLimit ? (
          <SerialPreviewContent
            serials={preview.data}
            isPending={preview.isPending}
            isError={preview.isError}
          />
        ) : (
          <Typography variant="caption">
            Too many serials to show, click to download
          </Typography>
        )
      }
    >
      <Link
        component="button"
        type="button"
        onClick={handleClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {props.label}
        <FileDownloadOutlined sx={{ fontSize: '0.9rem' }} color="action" />
      </Link>
    </Tooltip>
  );
}

/**
 * Renders the serial download links for an entry: the whole list as of this
 * event, plus whatever moved to produce it. A fresh set carries only the whole
 * list, so no delta is shown for it.
 */
function SerialDelta(props: {
  entry: ShipmentManifestEntry;
  operation?: string;
}) {
  const { entry } = props;
  const serialFileKey = entry.serialFileKey;
  const addedFileKey = entry.addedFileKey;
  const removedFileKey = entry.removedFileKey;
  const numberSerials = entry.numberSerials ?? null;
  const numberAdded = entry.numberAdded;
  const numberRemoved = entry.numberRemoved;
  // An item cleared to nothing records a count of zero and no file, so the
  // count alone decides whether the list is stated.
  const hasSerials = numberSerials !== null;
  const hasAdded = numberAdded != null && addedFileKey != null;
  const hasRemoved = numberRemoved != null && removedFileKey != null;
  if (!hasSerials && !hasAdded && !hasRemoved) {
    return null;
  }

  // Only claim pending or confirmed when the operation says which. An entry
  // recorded before the operation was captured says neither, and guessing
  // "confirmed" there would assert the one thing a reader consults this screen
  // to find out.
  let label = 'Serials';
  if (props.operation === SHIPMENT_AUDIT_OPERATIONS.PENDING_SERIALS_UPDATE) {
    label = 'Pending serials';
  } else if (props.operation !== undefined) {
    label = 'Confirmed serials';
  }

  return (
    <Typography
      variant="caption"
      marginLeft={4}
      display="flex"
      alignItems="center"
      columnGap={0.5}
      flexWrap="wrap"
    >
      {label}:&nbsp;
      {hasSerials &&
        (serialFileKey == null ? (
          <span>{pluralize(numberSerials, { singular: 'serial' })}</span>
        ) : (
          <ManifestFileLink
            label={pluralize(numberSerials, { singular: 'serial' })}
            fileKey={serialFileKey}
            serialCount={numberSerials}
          />
        ))}
      {hasSerials && (hasAdded || hasRemoved) && <span>·</span>}
      {hasAdded && (
        <ManifestFileLink
          label={`${numberAdded} added`}
          fileKey={addedFileKey}
          serialCount={numberAdded}
        />
      )}
      {hasAdded && hasRemoved && <span>·</span>}
      {hasRemoved && (
        <ManifestFileLink
          label={`${numberRemoved} removed`}
          fileKey={removedFileKey}
          serialCount={numberRemoved}
        />
      )}
    </Typography>
  );
}

interface ManifestUpdate {
  id: string;
  name: string;
  sku: string;
  kind: 'added' | 'removed' | 'updated';
  old: ShipmentManifestEntry | null;
  new: ShipmentManifestEntry | null;
}

function fieldChanged(
  a: string | number | null,
  b: string | number | null
): boolean {
  return (a ?? null) !== (b ?? null);
}

/**
 * Formats an item heading as "name (sku)", dropping the parenthetical for
 * legacy items with no SKU.
 */
function itemHeading(name: string, sku: string | null | undefined): string {
  return sku ? `${name} (${sku})` : name;
}

/**
 * Renders a shipment's line-item manifest as an old→new diff: each item is shown
 * as added, removed, or updated, with the fields that changed (quantity,
 * received quantity, external reference) and any serial movements linking to
 * downloadable CSVs.
 */
export function AuditShipmentManifestLabel(props: Props) {
  const oldEntries = props.property.oldValue ?? [];
  const newEntries = props.property.newValue ?? [];

  const updates: ManifestUpdate[] = [];

  oldEntries.forEach((oldEntry) => {
    const newEntry =
      newEntries.find((entry) => entry.item.id === oldEntry.item.id) ?? null;
    updates.push({
      id: oldEntry.item.id,
      name: oldEntry.item.name,
      sku: oldEntry.item.sku ?? '',
      kind: newEntry === null ? 'removed' : 'updated',
      old: oldEntry,
      new: newEntry,
    });
  });

  newEntries.forEach((newEntry) => {
    const seen = updates.some((update) => update.id === newEntry.item.id);
    if (!seen) {
      updates.push({
        id: newEntry.item.id,
        name: newEntry.item.name,
        sku: newEntry.item.sku ?? '',
        kind: 'added',
        old: null,
        new: newEntry,
      });
    }
  });

  const rendered = updates
    .map((update) => renderUpdate(update, props.operation))
    .filter((node) => node !== null);

  if (rendered.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>
      {rendered}
    </Stack>
  );
}

function renderUpdate(update: ManifestUpdate, operation?: string) {
  if (update.kind === 'added' && update.new !== null) {
    const entry = update.new;
    return (
      <Stack key={update.id}>
        <Typography variant="caption" marginLeft={2}>
          {itemHeading(entry.item.name, entry.item.sku)}{' '}
          <Typography
            component="span"
            variant="inherit"
            sx={{ textDecoration: 'underline' }}
          >
            added
          </Typography>
        </Typography>
        <Typography variant="caption" marginLeft={4}>
          Quantity:&nbsp;
          <AuditPropertyValue value={entry.quantity} />
        </Typography>
        {entry.receivedQuantity !== null && (
          <Typography variant="caption" marginLeft={4}>
            Received:&nbsp;
            <AuditPropertyValue value={entry.receivedQuantity} />
          </Typography>
        )}
        {entry.externalReference !== null && (
          <Typography variant="caption" marginLeft={4}>
            External reference:&nbsp;
            <AuditPropertyValue value={entry.externalReference} />
          </Typography>
        )}
        {entry.value !== null && (
          <Typography variant="caption" marginLeft={4}>
            Asset value:&nbsp;
            <AuditPropertyValue value={entry.value} />
          </Typography>
        )}
        <SerialDelta entry={entry} operation={operation} />
      </Stack>
    );
  }

  if (update.kind === 'removed' && update.old !== null) {
    const entry = update.old;
    return (
      <Stack key={update.id}>
        <Typography variant="caption" marginLeft={2}>
          {itemHeading(entry.item.name, entry.item.sku)}{' '}
          <Typography
            component="span"
            variant="inherit"
            sx={{ textDecoration: 'underline' }}
          >
            removed
          </Typography>
        </Typography>
        <Typography variant="caption" marginLeft={4}>
          Quantity:&nbsp;
          <AuditPropertyValue value={entry.quantity} />
        </Typography>
      </Stack>
    );
  }

  if (update.old === null || update.new === null) {
    return null;
  }

  const oldEntry = update.old;
  const newEntry = update.new;
  const showQuantity = fieldChanged(oldEntry.quantity, newEntry.quantity);
  const showReceived = fieldChanged(
    oldEntry.receivedQuantity,
    newEntry.receivedQuantity
  );
  const showReference = fieldChanged(
    oldEntry.externalReference,
    newEntry.externalReference
  );
  const showValue = fieldChanged(oldEntry.value, newEntry.value);
  const hasSerials =
    newEntry.numberAdded !== null ||
    newEntry.numberRemoved !== null ||
    (newEntry.numberSerials !== null && newEntry.numberSerials !== undefined);

  if (
    !showQuantity &&
    !showReceived &&
    !showReference &&
    !showValue &&
    !hasSerials
  ) {
    return null;
  }

  return (
    <Stack key={update.id}>
      <Typography variant="caption" marginLeft={2}>
        {itemHeading(update.name, update.sku)}{' '}
        <Typography
          component="span"
          variant="inherit"
          sx={{ textDecoration: 'underline' }}
        >
          updated
        </Typography>
      </Typography>
      {showQuantity && (
        <Typography variant="caption" component="div" marginLeft={4}>
          <AuditPropertyUpdateLabel
            name="Quantity"
            oldValue={oldEntry.quantity}
            newValue={newEntry.quantity}
          />
        </Typography>
      )}
      {showReceived && (
        <Typography variant="caption" component="div" marginLeft={4}>
          <AuditPropertyUpdateLabel
            name="Received"
            oldValue={oldEntry.receivedQuantity}
            newValue={newEntry.receivedQuantity}
          />
        </Typography>
      )}
      {showReference && (
        <Typography variant="caption" component="div" marginLeft={4}>
          <AuditPropertyUpdateLabel
            name="External reference"
            oldValue={oldEntry.externalReference}
            newValue={newEntry.externalReference}
          />
        </Typography>
      )}
      {showValue && (
        <Typography variant="caption" component="div" marginLeft={4}>
          <AuditPropertyUpdateLabel
            name="Asset value"
            oldValue={oldEntry.value}
            newValue={newEntry.value}
          />
        </Typography>
      )}
      <SerialDelta entry={newEntry} operation={operation} />
    </Stack>
  );
}
